import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  MapPin, 
  Briefcase, 
  Building2, 
  Calendar,
  ArrowLeft,
  Loader2,
  Star
} from 'lucide-react';

export default function PublicJobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await jobsAPI.getPublic();
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => 
    job.titulo.toLowerCase().includes(search.toLowerCase()) ||
    job.descricao.toLowerCase().includes(search.toLowerCase()) ||
    job.localizacao.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
                  <span className="text-cyan-400 font-bold text-sm">G2</span>
                </div>
                <span className="font-bold text-lg text-slate-900">Vagas</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/login')}
              >
                Entrar
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                Cadastre-se
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Search Section */}
      <section className="bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Encontre sua próxima oportunidade
          </h1>
          <p className="text-slate-300 mb-8">
            Vagas de empresas comprometidas com ética e inclusão
          </p>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Buscar por cargo, empresa ou localização..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-12 h-14 text-lg bg-white border-0"
              data-testid="jobs-search-input"
            />
          </div>
        </div>
      </section>

      {/* Jobs List */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className="text-center py-20">
              <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Nenhuma vaga encontrada
              </h3>
              <p className="text-slate-600">
                {search ? 'Tente ajustar sua busca' : 'Novas vagas serão publicadas em breve'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600 mb-6">
                {filteredJobs.length} vaga{filteredJobs.length !== 1 ? 's' : ''} encontrada{filteredJobs.length !== 1 ? 's' : ''}
              </p>
              
              {filteredJobs.map((job) => (
                <Card 
                  key={job.id} 
                  className="border-slate-200 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/jobs/${job.id}`)}
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {job.destaque && (
                            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                              <Star className="w-3 h-3 mr-1" />
                              Destaque
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-slate-600">
                            {job.tipo_contrato}
                          </Badge>
                        </div>
                        <h3 className="text-xl font-semibold text-slate-900 mb-2">
                          {job.titulo}
                        </h3>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-3">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {job.company_name || 'Empresa'}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.localizacao}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(job.created_at)}
                          </span>
                        </div>
                        <p className="text-slate-600 line-clamp-2">
                          {job.descricao}
                        </p>
                      </div>
                      <Button 
                        className="ml-4 bg-cyan-600 hover:bg-cyan-700"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/login');
                        }}
                      >
                        Candidatar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
